import { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'motion/react';
import { useTranslation } from 'react-i18next';
import { HiArrowDownRight } from 'react-icons/hi2';
import { MagneticButton } from './MagneticButton';
import { SplitWords } from './SplitWords';
import { EASE } from '../lib/motion';
import { useReady } from '../lib/ready';

/**
 * Kinetic-type hero. The 3D core owns the upper right of the viewport (see
 * BackdropScene poses); the headline is set bottom-left and overlaps it, so
 * type and object read as one composition.
 */
export function Hero() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const ready = useReady();
  const ref = useRef<HTMLElement>(null);

  // On scroll-out the two lines separate, giving the type its own depth.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -160]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, reduce ? 1 : 0]);

  const rise = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: ready ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: 0.9, ease: EASE, delay },
  });

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-[100dvh] flex-col justify-end px-5 pt-24 pb-10 md:px-8 md:pb-14"
    >
      <motion.div
        style={{ opacity: fade }}
        className="mx-auto w-full max-w-[1400px]"
      >
        <motion.p
          {...rise(0.05)}
          className="mb-6 font-mono text-xs uppercase tracking-[0.22em] text-accent md:mb-8"
        >
          {t('hero.kicker')}
        </motion.p>

        <h1 className="font-display text-[clamp(3rem,8.4vw,9rem)] font-semibold leading-[0.94] tracking-[-0.045em]">
          <motion.span style={{ y: y1 }} className="block">
            <SplitWords text={t('hero.title.line1')} play={ready} delay={0.1} />
          </motion.span>
          <motion.span
            style={{ y: y2 }}
            className="block text-accent md:pl-[11vw]"
          >
            <SplitWords text={t('hero.title.line2')} play={ready} delay={0.3} />
          </motion.span>
        </h1>

        <div className="mt-10 grid gap-8 md:mt-14 md:grid-cols-12 md:items-end">
          <motion.p
            {...rise(0.7)}
            className="max-w-[40ch] text-lg leading-relaxed text-muted md:col-span-5"
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.div
            {...rise(0.82)}
            className="flex flex-wrap items-center gap-4 md:col-span-7 md:justify-end"
          >
            <MagneticButton href="#contact">
              {t('hero.cta')}
              <HiArrowDownRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </MagneticButton>
            <MagneticButton href="#work" variant="ghost">
              {t('hero.secondary')}
            </MagneticButton>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
